/**
 * O VOCABULÁRIO DE ETIQUETAS DO SUITE.
 *
 * POR QUE ISTO EXISTE
 *
 *   `yard-ativos-marketing-crm-atendimento-ia.html` não descreve uma regra
 *   para colorir etiqueta: ele LISTA, uma a uma, qual variante cada palavra do
 *   produto usa. "Proposta enviada" é `--info`. "Proposta vencendo" é
 *   `--atencao`. "Contato feito" é `--contorno`. "Novo lead" é a neutra.
 *
 *   Sem esta tabela, cada tela inventa a própria regra a partir do que parece
 *   fazer sentido - e foi exatamente o que aconteceu: a coluna Estágio da
 *   lista de negócios ganhou uma rampa amarela pela POSIÇÃO da etapa no funil,
 *   que não existe em documento nenhum. Ficou coerente consigo mesma e
 *   diferente do sistema, que é a pior combinação possível.
 *
 *   A tabela abaixo é transcrição, não dedução. Palavra que não está aqui cai
 *   na neutra, que é o que o documento faz com "Novo lead" e "Qualificação".
 *
 * COMO ESTENDER
 *
 *   Estágio novo no documento entra aqui, em caixa baixa. Estágio que o
 *   cliente cria na mão (`#config`) NÃO entra: ele não é vocabulário do
 *   sistema, é palavra do cliente, e a neutra é a resposta certa para ela.
 */

/** A variante, sem o `pill` da frente. Vazio é a pílula neutra. */
export type VarianteDePilula =
  | ''
  | 'pill--contorno'
  | 'pill--info'
  | 'pill--atencao'
  | 'pill--erro'
  | 'pill--ok'
  | 'pill--marca';

const VOCABULARIO: Record<string, VarianteDePilula> = {
  /* ---- CRM · etapas do funil ---- */
  'novo lead': '',
  'qualificação': '',
  'qualificando': '',
  'contato feito': 'pill--contorno',
  'proposta enviada': 'pill--info',
  'proposta vencendo': 'pill--atencao',
  'negociação': 'pill--info',
  'aguardando aprovação': 'pill--contorno',
  'ganho': 'pill--ok',
  'perdido': 'pill--erro',
  'perdido · sem resposta': 'pill--erro',
  'pausado': 'pill--contorno',
  'reaberto': 'pill--marca',

  /* ---- CRM · origem do lead ---- */
  'veio do whatsapp': 'pill--info',
  'importado do csv': 'pill--contorno',
  'formulário do site': 'pill--contorno',
  'indicação': 'pill--contorno',
  'instagram ads': 'pill--contorno',
  'google ads': 'pill--contorno',
  'evento': 'pill--contorno',
  'lista fria': 'pill--contorno',

  /* ---- CRM · pagamento e contrato ---- */
  'pago': 'pill--ok',
  'pagamento recusado': 'pill--erro',
  'estorno solicitado': 'pill--atencao',
  'nota emitida': 'pill--info',
  'contrato assinado': 'pill--ok',
  'aguardando assinatura': 'pill--contorno',

  /* ---- Temperatura do lead (CRM e IA) ----
     O mesmo trio de `etiquetaDeTemperatura` (ui/etiquetas.ts), que é quem
     manda: quente "já está acontecendo" (erro), morno "vai esfriar se ninguém
     agir" (atenção), frio a faixa fria da régua (info). */
  quente: 'pill--erro',
  morno: 'pill--atencao',
  frio: 'pill--info',
  'dados insuficientes': 'pill--contorno',

  /* ---- Marketing · status de campanha ---- */
  rascunho: '',
  'em revisão': 'pill--contorno',
  aprovada: 'pill--ok',
  agendada: 'pill--info',
  'em veiculação': 'pill--marca',
  pausada: 'pill--atencao',
  concluída: 'pill--contorno',
  reprovada: 'pill--erro',

  /* ---- Marketing · teste A/B e calendário ---- */
  'variante a': 'pill--contorno',
  'variante b': 'pill--contorno',
  vencedora: 'pill--ok',
  'empate técnico': 'pill--info',
  ideia: '',
  'em produção': 'pill--contorno',
  agendado: 'pill--info',
  publicado: 'pill--ok',

  /* ---- Flow · atendimento ---- */
  'na fila': 'pill--contorno',
  'em atendimento': 'pill--info',
  'aguardando cliente': 'pill--atencao',
  resolvido: 'pill--ok',
  transferido: 'pill--contorno',
  'sla estourado': 'pill--erro',

  /* ---- Flow · presença do atendente ---- */
  online: 'pill--ok',
  ausente: 'pill--atencao',
  'em pausa': 'pill--contorno',
  offline: '',

  /* ---- IA · sentimento ---- */
  positivo: 'pill--ok',
  neutro: 'pill--contorno',
  negativo: 'pill--atencao',
  urgente: 'pill--erro',
};

/**
 * A variante da etiqueta para uma palavra do produto.
 *
 * Compara sem acento e sem caixa: o banco guarda "Negociacao" em algumas
 * contas e "Negociação" em outras, e as duas são a mesma palavra do sistema.
 * O que vem depois de " · " é ignorado, porque "Quente · 92" e "Perdido · sem
 * resposta" carregam contagem e motivo colados no rótulo.
 */
export function varianteDaEtiqueta(rotulo: string | null | undefined): VarianteDePilula {
  if (!rotulo) return '';
  const limpo = rotulo.trim().toLowerCase();
  const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

  const exato = VOCABULARIO[limpo];
  if (exato !== undefined) return exato;

  const alvo = semAcento(limpo);
  for (const [chave, v] of Object.entries(VOCABULARIO)) {
    if (semAcento(chave) === alvo) return v;
  }
  // "Quente · 92" cai em "quente"; "Perdido · sem resposta" já bateu acima.
  const antesDoPonto = alvo.split('·')[0]?.trim();
  if (antesDoPonto && antesDoPonto !== alvo) {
    for (const [chave, v] of Object.entries(VOCABULARIO)) {
      if (semAcento(chave) === antesDoPonto) return v;
    }
  }
  return '';
}

/** A classe pronta, com o `pill` da frente. */
export const classeDaEtiqueta = (rotulo: string | null | undefined) =>
  ['pill', varianteDaEtiqueta(rotulo)].filter(Boolean).join(' ');

/**
 * A etapa do funil, que tem uma regra a mais: `kind` manda.
 *
 * Um funil pode chamar a etapa de fechamento de "Fechado", "Assinado" ou
 * qualquer palavra que o cliente quiser em `#config` - e ela continua sendo
 * ganho. O tipo gravado no banco decide primeiro; o vocabulário responde pelo
 * resto, e a neutra responde pelo que o cliente inventou.
 */
export function classeDaEtapa(nome: string | null | undefined, kind: string | null | undefined) {
  if (kind === 'won') return 'pill pill--ok';
  if (kind === 'lost') return 'pill pill--erro';
  return classeDaEtiqueta(nome);
}
